// Summaries of past conversations (TOM-251, ADR 0250) and what the assistant
// learned from them (TOM-254, ADR 0258): the Profile page's client for
// /v1/neo/recall, the decoding, and the wording, the chat's one-time notice
// that Neo now reads them (TOM-252), and the chat's chips for the
// summaries a turn searched, opened or forgot (TOM-253).
//
// Once a chat with the assistant has been quiet for half an hour, the cloud
// writes a short summary of it: a title, a gist, the lasting facts the owner
// stated and what was left open. At most once a day, after the first summary
// and then after five new ones or a week, it folds the lasting ones into a
// short learned profile: facts and preferences about the owner and their
// boats. The owner sees every summary and every learned item here, can
// forget any of them, and can switch the feature off, which erases them all.
// They are the caller's alone; a co-owner of the same boat never sees them.
//
// Decoded defensively, like the notes: a malformed summary or item is
// dropped, never rendered half-filled.

import { api } from '../api';

/** One summary, as GET /v1/neo/recall lists it. Times are ISO strings. */
export interface NeoRecallEpisode {
  id: string;
  /** The conversation's boat; null for a chat with no boat. */
  boatId: string | null;
  /** Null unless the caller still owns that boat and it is active. */
  boatName: string | null;
  startedAt: string;
  endedAt: string;
  /**
   * The day it ended, `YYYY-MM-DD`, as Neo gives it (TOM-253): in its
   * boat's local time. Null from a server that does not send it; the list
   * then dates it by `endedAt` in the browser's zone.
   */
  day: string | null;
  title: string;
  gist: string;
  ownerFacts: string[];
  openThreads: string[];
}

/** The switch, and when it was last turned on. */
export interface NeoRecallSettings {
  enabled: boolean;
  since: string;
}

/** What a learned item is about, as the cloud files it. */
export type NeoProfileKind = 'preference' | 'boat_fact' | 'about_owner' | 'routine';

const PROFILE_KINDS: readonly NeoProfileKind[] = [
  'preference',
  'boat_fact',
  'about_owner',
  'routine',
];

/** One learned item, as GET /v1/neo/recall lists it. */
export interface NeoProfileItem {
  id: string;
  /** A short fact in the third person, such as "Prefers short answers." */
  text: string;
  /** Null for a kind this page does not know yet: the item is still shown. */
  kind: NeoProfileKind | null;
  /** The boat the fact is about; null when it is about the owner. */
  boatId: string | null;
  /** Null unless the caller still owns that boat and it is active. */
  boatName: string | null;
  /** When its wording last changed (ISO). */
  updatedAt: string;
}

export interface NeoRecallState extends NeoRecallSettings {
  /** Newest first. */
  episodes: NeoRecallEpisode[];
  /** Most recently confirmed first. */
  profile: NeoProfileItem[];
}

/** How many summaries the Profile page shows before "Show all". */
export const NEO_RECALL_FIRST_PAGE = 10;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function nullableStringOf(v: unknown): string | null | undefined {
  return v === null ? null : stringOf(v);
}

function stringList(v: unknown): string[] {
  return Array.isArray(v)
    ? v.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : [];
}

function isTime(v: string | undefined): v is string {
  return v !== undefined && !Number.isNaN(Date.parse(v));
}

/** Decode one listed summary, or null when a required field is missing. */
export function parseNeoRecallEpisode(raw: unknown): NeoRecallEpisode | null {
  if (!isRecord(raw)) return null;
  const id = stringOf(raw.id);
  const boatId = nullableStringOf(raw.boatId);
  const boatName = nullableStringOf(raw.boatName);
  const startedAt = stringOf(raw.startedAt);
  const endedAt = stringOf(raw.endedAt);
  const day = stringOf(raw.day);
  const title = stringOf(raw.title);
  const gist = stringOf(raw.gist);
  if (id === undefined || title === undefined || gist === undefined) return null;
  if (!isTime(startedAt) || !isTime(endedAt)) return null;
  return {
    id,
    boatId: boatId ?? null,
    boatName: boatName ?? null,
    startedAt,
    endedAt,
    day: isDay(day) ? day : null,
    title,
    gist,
    ownerFacts: stringList(raw.ownerFacts),
    openThreads: stringList(raw.openThreads),
  };
}

/**
 * Decode one learned item, or null when its id, text or time is missing. A
 * kind this page does not know reads as null rather than dropping the item:
 * the owner must still see it to forget it.
 */
export function parseNeoProfileItem(raw: unknown): NeoProfileItem | null {
  if (!isRecord(raw)) return null;
  const id = stringOf(raw.id);
  const text = stringOf(raw.text);
  const updatedAt = stringOf(raw.updatedAt);
  if (id === undefined || text === undefined || !isTime(updatedAt)) return null;
  const kind = PROFILE_KINDS.find((k) => k === raw.kind) ?? null;
  return {
    id,
    text,
    kind,
    boatId: nullableStringOf(raw.boatId) ?? null,
    boatName: nullableStringOf(raw.boatName) ?? null,
    updatedAt,
  };
}

/** Decode the switch, or null when it is malformed. */
export function parseNeoRecallSettings(payload: unknown): NeoRecallSettings | null {
  if (!isRecord(payload) || typeof payload.enabled !== 'boolean') return null;
  const since = stringOf(payload.since);
  if (!isTime(since)) return null;
  return { enabled: payload.enabled, since };
}

/** Decode a list, dropping every entry `parse` refuses; a missing list is empty. */
function listOf<T>(raw: unknown, parse: (entry: unknown) => T | null): T[] {
  const out: T[] = [];
  for (const entry of Array.isArray(raw) ? raw : []) {
    const parsed = parse(entry);
    if (parsed !== null) out.push(parsed);
  }
  return out;
}

/** Decode GET /v1/neo/recall, or null when the switch itself is malformed. */
export function parseNeoRecall(payload: unknown): NeoRecallState | null {
  const settings = parseNeoRecallSettings(payload);
  if (settings === null || !isRecord(payload)) return null;
  return {
    ...settings,
    episodes: listOf(payload.episodes, parseNeoRecallEpisode),
    profile: listOf(payload.profile, parseNeoProfileItem),
  };
}

// ----- Client --------------------------------------------------------------

/** The switch and every kept summary. Throws on a failed or malformed reply. */
export async function loadNeoRecall(): Promise<NeoRecallState> {
  const state = parseNeoRecall(await api<unknown>('/v1/neo/recall'));
  if (state === null) throw new Error('malformed /v1/neo/recall reply');
  return state;
}

/**
 * Forget one summary. A 404 means it is already gone (forgotten in another tab,
 * or erased by switching off) and resolves like a success: either way the list
 * should drop it. Any other failure throws.
 */
export async function forgetNeoRecallEpisode(id: string): Promise<void> {
  try {
    await api(`/v1/neo/recall/episodes/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return;
    throw err;
  }
}

/**
 * Forget one learned item, so Neo doesn't learn it again. A 404 means it
 * is already gone (forgotten in another tab, hidden because a summary it came
 * from was forgotten, or erased by switching off) and resolves like a success.
 * Any other failure throws.
 */
export async function forgetNeoProfileItem(id: string): Promise<void> {
  try {
    await api(`/v1/neo/recall/profile/${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return;
    throw err;
  }
}

/**
 * Switch summaries on or off. Off erases every summary and everything learned
 * from them. Returns the new switch.
 */
export async function saveNeoRecallEnabled(enabled: boolean): Promise<NeoRecallSettings> {
  const settings = parseNeoRecallSettings(
    await api<unknown>('/v1/neo/recall/settings', { method: 'PUT', body: { enabled } })
  );
  if (settings === null) throw new Error('malformed /v1/neo/recall/settings reply');
  return settings;
}

// ----- The switch ----------------------------------------------------------

/** What a press on the switch does. */
export type RecallSwitchPress = 'confirm_off' | 'turn_on' | 'none';

/**
 * Switching off erases every summary, so a press while on only asks first
 * (the confirm then calls the PUT); a press while off turns it on. Nothing
 * while the switch is unknown or a save is in flight.
 */
export function recallSwitchPress(
  settings: NeoRecallSettings | null,
  busy: boolean
): RecallSwitchPress {
  if (settings === null || busy) return 'none';
  return settings.enabled ? 'confirm_off' : 'turn_on';
}

/**
 * The section after the server answered a switch change: the switch as
 * stored, and no summaries or learned items once it is off (the server erased
 * them).
 */
export function recallAfterSave(
  prev: NeoRecallState | null,
  settings: NeoRecallSettings
): NeoRecallState | null {
  if (prev === null) return prev;
  return settings.enabled
    ? { ...settings, episodes: prev.episodes, profile: prev.profile }
    : { ...settings, episodes: [], profile: [] };
}

/**
 * The section once a summary is forgotten. The learned items it was a source
 * of are deleted by the server with it, so the caller re-reads the section to
 * drop them; until then they stay listed.
 */
export function recallWithoutEpisode(
  prev: NeoRecallState | null,
  id: string
): NeoRecallState | null {
  return prev === null ? prev : { ...prev, episodes: prev.episodes.filter((e) => e.id !== id) };
}

/**
 * The section with the learned list as `next` (a fresh read) has it, and
 * everything else as shown: a summary forgotten while the read was in flight
 * stays gone.
 */
export function recallWithProfileFrom(
  prev: NeoRecallState | null,
  next: NeoRecallState
): NeoRecallState | null {
  return prev === null ? prev : { ...prev, profile: prev.enabled ? next.profile : [] };
}

/** The section once a learned item is forgotten. */
export function recallWithoutProfileItem(
  prev: NeoRecallState | null,
  id: string
): NeoRecallState | null {
  return prev === null ? prev : { ...prev, profile: prev.profile.filter((p) => p.id !== id) };
}

// ----- Wording -------------------------------------------------------------

/**
 * How long a summary is kept after its chat ended: the server's
 * NEO_RECALL_EPISODE_RETENTION_DAYS (TOM-255), after which the nightly run
 * expires it and it leaves the list. tests/core/neo/recall/prune.spec.ts fails
 * when the two differ.
 */
export const NEO_RECALL_RETENTION_DAYS = 365;

const DAY_MS = 24 * 60 * 60_000;

/**
 * The help line under "Past conversations". Since TOM-252 each chat turn reads
 * a few summaries into its answer, the most recent ones and any that match the
 * question; since TOM-253, asked about an earlier chat, Neo can search all
 * of them, read that chat back, or forget one; and since TOM-255 each is kept
 * for a year after its chat ended. The line says all of it.
 */
export const NEO_RECALL_HELP =
  'Short summaries of your past chats, written once a chat has been quiet for half an hour, ' +
  'so Neo can pick up where you left off. With each question it reads a few of them: ' +
  'the most recent, and any that match what you ask. When you ask about an earlier chat, it ' +
  'can search all of them and read that chat back. Each is kept for a year after the chat ' +
  'ends. Forget any you’d rather it didn’t keep, here or by asking Neo.';

function formatDay(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The line under a summary's title: the day the chat went quiet, and its boat.
 * The day is the one Neo gives (`day`), so the list, the chat's chip and
 * the answer that names the chat agree; an older reply without it is dated by
 * `endedAt` in the browser's zone.
 */
export function episodeMeta(episode: NeoRecallEpisode, locale?: string): string {
  const day =
    episode.day === null
      ? formatDay(episode.endedAt, locale)
      : formatRecallDay(episode.day, locale);
  return episode.boatName === null ? day : `${day} · ${episode.boatName}`;
}

/**
 * The line under the switch. Summaries expire a year after their chat ended
 * (TOM-255), so once the switch has been on for longer than that, the line
 * says it keeps the past year's rather than naming a day whose summaries are
 * gone.
 */
export function recallStatusLine(
  settings: NeoRecallSettings,
  locale?: string,
  now: Date = new Date()
): string {
  if (!settings.enabled) return 'Off. Neo isn’t keeping summaries of your chats.';
  const since = Date.parse(settings.since);
  const kept = now.getTime() - NEO_RECALL_RETENTION_DAYS * DAY_MS;
  if (!Number.isNaN(since) && since < kept) {
    return 'On. Keeping summaries of the past year’s chats.';
  }
  return `On. Keeping summaries of chats since ${formatDay(settings.since, locale)}.`;
}

// ----- The chat's one-time notice (TOM-252) --------------------------------
//
// Once Neo reads summaries into its answers, an owner whose summaries are
// on is told so once, in the chat, with a link to where they can see and
// forget them. The chat learns the switch from GET /v1/neo/context's
// `recallEnabled`, not from a call to /v1/neo/recall. The dismissal is kept in
// localStorage per account, so each account on a shared browser is told once.

/** The notice. The second sentence links to /profile. */
export const NEO_RECALL_NOTICE = 'Neo now remembers your past conversations.';
export const NEO_RECALL_NOTICE_LINK = 'See or forget them on your Profile.';

type NoticeStorage = Pick<Storage, 'getItem' | 'setItem'>;

const localStore = (): NoticeStorage => window.localStorage;

/** The localStorage key holding one account's dismissal. */
export function neoRecallNoticeKey(userId: string): string {
  return `tombolo.neo.recall-notice.${userId}`;
}

/**
 * Whether this account dismissed the notice. Storage that cannot be read
 * counts as not dismissed: the notice then shows until it is dismissed on the
 * page, rather than never.
 */
export function readNeoRecallNoticeDismissed(
  userId: string,
  storage: () => NoticeStorage = localStore
): boolean {
  try {
    return storage().getItem(neoRecallNoticeKey(userId)) !== null;
  } catch {
    return false;
  }
}

/** Keep this account's dismissal. Blocked storage keeps it for the page only. */
export function dismissNeoRecallNotice(
  userId: string,
  storage: () => NoticeStorage = localStore
): void {
  try {
    storage().setItem(neoRecallNoticeKey(userId), '1');
  } catch {
    // Storage blocked or full: the chat still hides it until the next load.
  }
}

/**
 * The chat shows the notice while the owner's summaries are on and the notice
 * has not been dismissed. `recallEnabled` is the context's field; anything but
 * a true there (an older reply without it, say) shows nothing.
 */
export function showNeoRecallNotice(recallEnabled: unknown, dismissed: boolean): boolean {
  return recallEnabled === true && !dismissed;
}

/**
 * The confirm shown before switching off, which erases `count` summaries and
 * the `learned` items learned from them. The body says the learned items go
 * too even when none is listed: a run may be writing some as the switch goes
 * off, and the server erases those as well.
 */
export function recallOffConfirm(
  count: number,
  learned = 0
): { title: string; body: string; action: string } {
  if (count === 0 && learned === 0) {
    return {
      title: 'Stop remembering past conversations?',
      body:
        'Neo will stop keeping summaries of your chats and learning about you from them, ' +
        'and erase anything it has learned. Your saved notes stay.',
      action: 'Turn off',
    };
  }
  const what: string[] = [];
  if (count > 0) what.push(count === 1 ? '1 summary' : `all ${count} summaries`);
  if (learned > 0) what.push(learned === 1 ? '1 learned fact' : `${learned} learned facts`);
  return {
    title: `Turn off and erase ${what.join(' and ')}?`,
    body:
      'Neo will stop keeping summaries of your chats and erase the ones it has, along ' +
      'with everything it learned about you from them. This can’t be undone. Your saved ' +
      'notes stay.',
    action: 'Turn off and erase',
  };
}

// ----- Learned about you (TOM-254, ADR 0258) -------------------------------

/**
 * The help line under "Learned about you". Since TOM-254 each of the owner's
 * own chat turns reads these into its answer (ADR 0258), so the line says
 * what they are for, and that nobody else sees them.
 */
export const NEO_PROFILE_HELP =
  'Lasting facts Neo has picked up from your conversations, such as how you like your ' +
  'answers or what’s aboard your boat, kept so it won’t have to ask you again. Neo ' +
  'uses them when it answers you. Only you can see them, and you can forget any of them.';

/**
 * The line in place of an empty "Learned about you". The first summary of a
 * chat already makes the cloud's first update due (ADR 0258 §3), so it says
 * "after", not "once there are a few".
 */
export const NEO_PROFILE_EMPTY =
  'Nothing yet. After your chats are summarised, the lasting facts Neo picks up from ' +
  'them appear here.';

const PROFILE_KIND_LABELS: Record<NeoProfileKind, string> = {
  preference: 'Preference',
  boat_fact: 'Boat',
  about_owner: 'About you',
  routine: 'Routine',
};

/** The short label for an item's kind, or null for a kind this page does not know. */
export function profileKindLabel(kind: NeoProfileKind | null): string | null {
  return kind === null ? null : PROFILE_KIND_LABELS[kind];
}

/** The line under a learned item: its kind, and its boat when there is one. */
export function profileItemMeta(item: NeoProfileItem): string {
  return [profileKindLabel(item.kind), item.boatName]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

// ----- The chat's chips (TOM-253) ------------------------------------------
//
// A chat turn can search the owner's summaries, open one to read what was
// said, or forget one. The cloud reports each as a recall record, whatever
// the answer says: on the stream as a `recall` event { lookups }, on the
// blocking body as `recalled`. A search reports only how many it found, never
// what. Asked to forget everything, a turn also forgets every fact learned
// about the owner, and reports only how many (TOM-254). Decoded defensively,
// like the summaries: a malformed record is dropped, never rendered
// half-filled.

/**
 * One recall record. `date` is the day the conversation ended, `YYYY-MM-DD`,
 * as Neo gives it: in its boat's local time (TOM-253).
 */
export type NeoRecallLookup =
  | { action: 'searched'; count: number }
  | { action: 'opened' | 'forgotten'; episodeId: string; date: string; title: string }
  | { action: 'forgotten_learned'; count: number };

/**
 * Fired on `window` when a chat turn forgot a summary or the learned facts, so
 * an open Profile refetches.
 */
export const NEO_RECALL_UPDATED_EVENT = 'nm:neo-recall-updated';

/** A `YYYY-MM-DD` that names a real day. */
function isDay(v: string | undefined): v is string {
  if (v === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  // Rejects a day the calendar lacks (2026-02-30), which Date rolls forward.
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

function parseRecallLookup(raw: unknown): NeoRecallLookup | null {
  if (!isRecord(raw)) return null;
  if (raw.action === 'searched') {
    const count = raw.count;
    return typeof count === 'number' && Number.isInteger(count) && count >= 0
      ? { action: 'searched', count }
      : null;
  }
  if (raw.action === 'forgotten_learned') {
    // Reported only when at least one was forgotten.
    const count = raw.count;
    return typeof count === 'number' && Number.isInteger(count) && count > 0
      ? { action: 'forgotten_learned', count }
      : null;
  }
  if (raw.action !== 'opened' && raw.action !== 'forgotten') return null;
  const episodeId = stringOf(raw.episodeId);
  const date = stringOf(raw.date);
  const title = stringOf(raw.title);
  if (episodeId === undefined || title === undefined || !isDay(date)) return null;
  return { action: raw.action, episodeId, date, title };
}

/** Decode a `recall` event's `lookups` (or the blocking route's `recalled`). */
export function parseRecallLookups(payload: unknown): NeoRecallLookup[] {
  const list = isRecord(payload) ? payload.lookups : payload;
  const out: NeoRecallLookup[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    const lookup = parseRecallLookup(raw);
    if (lookup !== null) out.push(lookup);
  }
  return out;
}

/**
 * Whether the turn forgot a summary or the learned facts, which the Profile's
 * lists must drop.
 */
export function recallForgotAny(lookups: readonly NeoRecallLookup[]): boolean {
  return lookups.some(
    (lookup) => lookup.action === 'forgotten' || lookup.action === 'forgotten_learned'
  );
}

/**
 * A `YYYY-MM-DD` the cloud already dated (the boat's local day), as the
 * Profile shows a day. Formatted as a calendar day, read at UTC midnight with
 * a UTC zone, so it shows the same day in every browser: local midnight would
 * show the day before west of Greenwich.
 */
export function formatRecallDay(day: string, locale?: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The chat chips for a turn's recall records, in the order they happened. All
 * the turn's searches share one chip, since a search says nothing about what
 * it found, and a summary opened or forgotten twice shows once. The learned
 * facts forgotten show once, by count. Each is plain text: the chat prints it
 * through recallChipMessages, never as Markdown.
 */
export function recallChips(lookups: readonly NeoRecallLookup[], locale?: string): string[] {
  const seen = new Set<string>();
  const chips: string[] = [];
  for (const lookup of lookups) {
    const key =
      lookup.action === 'searched' || lookup.action === 'forgotten_learned'
        ? lookup.action
        : `${lookup.action}:${lookup.episodeId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    switch (lookup.action) {
      case 'searched':
        chips.push('✓ Searched past conversations');
        break;
      case 'opened':
        chips.push(`✓ Looked back at: ${lookup.title} (${formatRecallDay(lookup.date, locale)})`);
        break;
      case 'forgotten':
        chips.push(`✓ Forgot: ${lookup.title}`);
        break;
      case 'forgotten_learned':
        chips.push(
          lookup.count === 1 ? '✓ Forgot 1 learned fact' : `✓ Forgot ${lookup.count} learned facts`
        );
        break;
    }
  }
  return chips;
}

/** A chip as the chat appends it to the transcript. */
export interface RecallChipMessage {
  id: string;
  role: 'assistant';
  content: string;
  local: true;
  /**
   * Printed as typed, never as Markdown. A title is stored text the summary
   * job wrote from the conversation, a pasted email's included, and a chip
   * never passes the output gate: as Markdown, `![](https://…)` in a title
   * would be an image the browser fetches with no click.
   */
  plain: true;
}

/** The chat's chip messages for a turn's recall records, after the answer `assistantId`. */
export function recallChipMessages(
  lookups: readonly NeoRecallLookup[],
  assistantId: string,
  locale?: string
): RecallChipMessage[] {
  return recallChips(lookups, locale).map((content, i) => ({
    id: `${assistantId}-recall-${i}`,
    role: 'assistant',
    content,
    local: true,
    plain: true,
  }));
}
